import type { ReactNode } from "react";
import { Card } from "./Card";
import { StatusBadge } from "../StatusBadge";
import type { AssessmentStatusType, StreamingProgress } from "../StatusBadge";
import { mutedText } from "@/styles/variants";
import { buttonVariants } from "@markeroo/theme";
import { ui } from "@/strings";
import { formatDistanceToNow, format } from "date-fns";
import PiAlertTriangleStroke from "@markeroo/icons/src/stroke/alerts/PiAlertTriangleStroke";

export interface DocumentCardAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "danger" | "ghost" | "ghostDanger" | "link";
  loading?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  dataTutorialTarget?: string;
}

interface DocumentCardProps {
  studentName: string;
  status: AssessmentStatusType;
  documentStatus?: "draft" | "final";
  issueCount?: number | null;
  actions?: DocumentCardAction[];
  children?: ReactNode;
  disabled?: boolean;
  createdAt?: string;
  emailBounced?: boolean;
  bounceReason?: string | null;
  streamingProgress?: StreamingProgress | null;
}

export function DocumentCard({
  studentName,
  status,
  documentStatus,
  issueCount,
  actions = [],
  children,
  disabled = false,
  createdAt,
  emailBounced = false,
  bounceReason,
  streamingProgress,
}: DocumentCardProps) {
  const getButtonVariant = (variant: DocumentCardAction["variant"], isFirst: boolean) => {
    if (variant === "danger") return buttonVariants.danger;
    if (variant === "ghostDanger") return buttonVariants.ghostDanger;
    if (variant === "ghost") return buttonVariants.ghost;
    if (variant === "link") return buttonVariants.link;
    if (variant === "secondary") return buttonVariants.secondary;
    if (variant === "primary") return buttonVariants.primary;
    // Default: first action is primary, rest are secondary
    return isFirst ? buttonVariants.primary : buttonVariants.secondary;
  };

  const isAssessing = status === "assessing";

  return (
    <div className={disabled ? "opacity-50 pointer-events-none" : ""} data-component="document-card">
      <Card className={isAssessing ? "glow-border-processing" : undefined}>
        <Card.Content>
          <Card.Title>
            {studentName}
            {documentStatus && (
              <span className="text-theme-muted font-normal">
                {" • "}
                {documentStatus === "draft" ? ui.labels.draft : ui.labels.final}
              </span>
            )}
          </Card.Title>
          {createdAt && (
            <div
              className={`${mutedText} mt-1`}
              title={`${ui.labels.paper} ${ui.labels.createdAt.toLowerCase()} at ${format(new Date(createdAt), "PPpp")}`}
            >
              {ui.labels.submitted} {formatDistanceToNow(new Date(createdAt), { addSuffix: true })}
            </div>
          )}
          {emailBounced && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-surface-warning border border-warning text-text-warning text-sm"
              title={bounceReason ?? ui.labels.emailBouncedTooltip}
            >
              <PiAlertTriangleStroke size={16} className="text-semantic-warning" />
              <span className="font-medium">{ui.labels.emailBounced}</span>
            </div>
          )}
          {children && (
            <div className="mt-8 border-t border-theme-primary pt-3">
              {children}
            </div>
          )}
        </Card.Content>

        <Card.TopRight stackOnMobile>
          <StatusBadge status={status} issueCount={issueCount} streamingProgress={streamingProgress} />
        </Card.TopRight>

        {actions.length > 0 && (
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            {actions.map((action, index) => (
              <button
                key={index}
                type="button"
                className={`${getButtonVariant(action.variant, index === 0)} w-full sm:w-auto`}
                onClick={action.onClick}
                disabled={action.loading || action.disabled}
                data-tutorial-target={action.dataTutorialTarget}
              >
                {action.loading ? (
                  ui.loading.processing
                ) : (
                  <>
                    {action.icon && <span className="mr-1.5 inline-flex items-center">{action.icon}</span>}
                    {action.label}
                  </>
                )}
              </button>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
